import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Send, MapPin } from "lucide-react";

export default function ContactSection() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", interest: "accompagnement", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
    if (!fullName || !form.email) { toast.error("Prénom, nom et email requis"); return; }
    setLoading(true);
    try {
      await api.post("/contact", { ...form, name: fullName });
      toast.success("Message envoyé ! Nous revenons vers vous très vite.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", interest: "accompagnement", message: "" });
    } catch {
      toast.error("Une erreur est survenue, réessayez.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Contact</div>
          <h2 className="display mt-3 text-3xl sm:text-5xl text-[#0A1128] leading-[1.05]">
            Parlons de votre projet.
          </h2>
          <p className="mt-5 text-[#475569] text-lg">
            Une question, un besoin précis, un appel d'offre ? Écrivez-nous, on répond sous 24h ouvrées.
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-black/5">
              <Mail size={20} className="text-[#06B6D4] mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-[#64748B]">Email</div>
                <div className="display font-bold text-[#0A1128]">contact@besson-solutions.fr</div>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-black/5">
              <MapPin size={20} className="text-[#06B6D4] mt-0.5" />
              <div>
                <div className="text-xs uppercase tracking-wider text-[#64748B]">Zone d'intervention</div>
                <div className="display font-bold text-[#0A1128]">France entière · DOM-TOM · Corse</div>
                <div className="text-xs text-[#64748B] mt-0.5">Visio, présentiel, hybride</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-white rounded-3xl border border-black/8 p-6 sm:p-8 shadow-sm" data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cfirst">Prénom *</Label>
              <Input id="cfirst" data-testid="contact-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="clast">Nom *</Label>
              <Input id="clast" data-testid="contact-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="cemail">Email *</Label>
              <Input id="cemail" type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <Label htmlFor="cphone">Téléphone</Label>
              <Input id="cphone" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="ccompany">Entreprise</Label>
              <Input id="ccompany" data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Type de besoin</Label>
              <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                <SelectTrigger data-testid="contact-interest"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accompagnement">Accompagnement commercial / marketing</SelectItem>
                  <SelectItem value="creation">Création de concept / MVP / CRM / ERP</SelectItem>
                  <SelectItem value="both">Les deux</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cmessage">Message</Label>
              <Textarea id="cmessage" rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Décrivez votre besoin en quelques lignes..." />
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="contact-submit" className="mt-5 w-full sm:w-auto bg-[#0A1128] hover:bg-[#14213d] text-white rounded-full px-7 py-6">
            <Send size={16} className="mr-2" /> {loading ? "Envoi..." : "Envoyer mon message"}
          </Button>
        </form>
      </div>
    </section>
  );
}
